import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Activity, TrendingDown, BarChart3, AlertTriangle, FileDown, Package } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine,
} from "recharts";
import {
  listPatients,
  patientPoints,
  aggregateByWeek,
  summarize,
  dayLabel,
  materialsSummary,
} from "@/lib/analysis";
import { exportAnalysisPDF } from "@/lib/analysis-pdf";
import { LEVELS } from "@/lib/priority";

const RANGES = [
  { key: "7d", label: "Ultimos 7 dias", days: 7 },
  { key: "4w", label: "Ultimas 4 semanas", days: 28 },
  { key: "3m", label: "Ultimos 3 meses", days: 90 },
  { key: "all", label: "Todo el historial", days: null },
];

const GROUPS = [
  { key: "day", label: "Por dia" },
  { key: "week", label: "Por semana" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 ${className}`}>
      {children}
    </div>
  );
}

function SecHdr({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#1D4ED8] text-white">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <h2 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
    </div>
  );
}

function StatPill({ label, value, color, bg }) {
  return (
    <div
      className="flex items-center justify-between rounded-xl px-3 py-2"
      style={{ backgroundColor: bg, color }}
    >
      <span className="text-xs font-semibold">{label}</span>
      <span className="text-base font-extrabold tabular-nums">{value}</span>
    </div>
  );
}

// Custom tooltip with units
function ChartTooltip({ unit }) {
  return ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
        <div className="mb-1 font-bold text-slate-900">{label}</div>
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-600">{entry.name}:</span>
            <span className="font-semibold tabular-nums text-slate-900">
              {entry.value == null ? "-" : Number(entry.value).toFixed(unit === "ml" ? 0 : 1)} {unit}
            </span>
          </div>
        ))}
      </div>
    );
  };
}

function TrendChart({ data, xKey, lines, unit, yDomain, refBands, refLines, height = 240, emptyHint }) {
  const hasData = data.some((d) => lines.some((l) => Number.isFinite(d[l.dataKey])));
  if (!hasData) {
    return (
      <div className="flex h-40 flex-col items-center justify-center rounded-xl bg-slate-50 text-center text-xs text-slate-500">
        <TrendingDown className="mb-1 h-5 w-5 text-slate-400" />
        <span>{emptyHint || "Sin datos en este rango"}</span>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        {refBands?.map((b, i) => (
          <ReferenceArea
            key={i}
            y1={b.y1}
            y2={b.y2}
            fill={b.fill}
            fillOpacity={0.18}
            ifOverflow="extendDomain"
          />
        ))}
        {refLines?.map((rl, i) => (
          <ReferenceLine
            key={`rl-${i}`}
            y={rl.y}
            stroke={rl.stroke}
            strokeDasharray="4 4"
            strokeWidth={1.5}
            label={rl.label ? { value: rl.label, position: "insideTopRight", fill: rl.stroke, fontSize: 10 } : undefined}
          />
        ))}
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} stroke="#64748B" />
        <YAxis
          domain={yDomain || ["auto", "auto"]}
          tick={{ fontSize: 11 }}
          stroke="#64748B"
          width={48}
        />
        <Tooltip content={ChartTooltip({ unit })} />
        <Legend wrapperStyle={{ fontSize: 12 }} iconType="line" />
        {lines.map((l) => (
          <Line
            key={l.dataKey}
            type="monotone"
            dataKey={l.dataKey}
            name={l.name}
            stroke={l.stroke}
            strokeWidth={2.5}
            dot={{ r: 3, fill: l.stroke }}
            activeDot={{ r: 5 }}
            connectNulls
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function AnalysisPage() {
  const nav = useNavigate();
  const [programs, setPrograms] = useState(null);
  const [patient, setPatient] = useState("");
  const [rangeKey, setRangeKey] = useState("4w");
  const [groupKey, setGroupKey] = useState("week");
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get("/programs");
      setPrograms(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando programas");
      setPrograms([]);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const patients = useMemo(() => listPatients(programs || []), [programs]);

  // Auto-pick the first patient when data is loaded
  useEffect(() => {
    if (!patient && patients.length > 0) setPatient(patients[0].name);
  }, [patient, patients]);

  const { fromDate, toDate } = useMemo(() => {
    const r = RANGES.find((x) => x.key === rangeKey) || RANGES[1];
    return {
      fromDate: r.days != null ? daysAgoISO(r.days) : "",
      toDate: todayISO(),
    };
  }, [rangeKey]);

  const dailyPoints = useMemo(
    () => patientPoints(programs || [], patient, fromDate, toDate),
    [programs, patient, fromDate, toDate]
  );
  const weeklyPoints = useMemo(() => aggregateByWeek(dailyPoints), [dailyPoints]);
  const summary = useMemo(() => summarize(dailyPoints), [dailyPoints]);
  const materials = useMemo(
    () => materialsSummary(programs || [], patient, fromDate, toDate),
    [programs, patient, fromDate, toDate]
  );

  const chartData = useMemo(() => {
    if (groupKey === "week") {
      return weeklyPoints.map((w) => ({ ...w, x: w.label }));
    }
    return dailyPoints.map((p) => ({ ...p, x: dayLabel(p.date) }));
  }, [groupKey, weeklyPoints, dailyPoints]);

  if (programs === null) {
    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <AppHeader subtitle="Analisis clinico" />
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (patients.length === 0) {
    return (
      <div className="min-h-screen bg-[#F1F5F9]">
        <AppHeader subtitle="Analisis clinico" />
        <main className="mx-auto max-w-3xl px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => nav("/dashboard")}
            className="mb-3 h-9 gap-1 px-2 text-sm font-semibold text-slate-700"
            data-testid="analysis-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al panel
          </Button>
          <Card>
            <div className="flex flex-col items-center py-8 text-center">
              <Activity className="mb-2 h-10 w-10 text-slate-300" />
              <h2 className="text-lg font-bold text-slate-900">Sin datos para analizar</h2>
              <p className="mt-1 text-sm text-slate-500">
                Aun no hay programas con nombre de paciente capturado. Crea programas en el panel principal para ver tendencias aqui.
              </p>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AppHeader subtitle="Analisis clinico" />
      <main className="mx-auto max-w-3xl space-y-5 px-4 py-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => nav("/dashboard")}
            className="h-9 gap-1 px-2 text-sm font-semibold text-slate-700"
            data-testid="analysis-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al panel
          </Button>
          <Button
            onClick={async () => {
              if (!patient) {
                toast.error("Selecciona un paciente primero");
                return;
              }
              setExporting(true);
              try {
                await exportAnalysisPDF({
                  patient,
                  rangeKey,
                  groupKey,
                  summary,
                  pointsCount: dailyPoints.length,
                  materials,
                });
                toast.success("PDF generado");
              } catch (e) {
                console.error(e);
                toast.error("No se pudo generar el PDF");
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting || !patient || dailyPoints.length === 0}
            className="h-10 rounded-xl bg-[#1D4ED8] px-4 text-sm font-semibold text-white shadow-sm hover:bg-[#1E40AF] disabled:opacity-60"
            data-testid="analysis-pdf-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generando...
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" /> Descargar PDF para imprimir
              </>
            )}
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Paciente
              </label>
              <Select value={patient} onValueChange={setPatient}>
                <SelectTrigger className="h-11 text-base" data-testid="analysis-patient-select">
                  <SelectValue placeholder="Selecciona paciente" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.name} value={p.name}>
                      {p.name} <span className="ml-1 text-xs text-slate-500">({p.count})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Periodo
              </label>
              <Select value={rangeKey} onValueChange={setRangeKey}>
                <SelectTrigger className="h-11 text-base" data-testid="analysis-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGES.map((r) => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Agrupar
              </label>
              <div className="flex gap-2" data-testid="analysis-group-toggle">
                {GROUPS.map((g) => {
                  const active = g.key === groupKey;
                  return (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => setGroupKey(g.key)}
                      className={`h-11 flex-1 rounded-xl border px-3 text-sm font-semibold transition-all ${
                        active
                          ? "border-[#1D4ED8] bg-[#1D4ED8] text-white shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-[#1D4ED8]"
                      }`}
                      data-testid={`analysis-group-${g.key}`}
                    >
                      {g.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>

        {/* Summary */}
        <Card>
          <SecHdr
            icon={BarChart3}
            title={`Resumen del periodo (${dailyPoints.length} turnos)`}
            subtitle={
              summary.totalShifts
                ? `Del ${summary.firstDate} al ${summary.lastDate} - ${summary.nurses.length} enfermera(s)`
                : "Sin turnos en el rango seleccionado"
            }
          />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(["red", "yellow", "green", "gray"]).map((k) => (
              <StatPill
                key={k}
                label={LEVELS[k].label}
                value={summary.priorityCounts[k] || 0}
                color={LEVELS[k].color}
                bg={LEVELS[k].bg}
              />
            ))}
          </div>
          {summary.nurses.length > 0 && (
            <div className="mt-3 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">Enfermeras:</span> {summary.nurses.join(", ")}
            </div>
          )}
        </Card>

        {/* Consumo de material */}
        <Card>
          <SecHdr
            icon={Package}
            title="Consumo de material"
            subtitle={
              materials.length
                ? `${materials.length} insumo(s) distintos registrados en el periodo seleccionado`
                : "Sin material registrado en el periodo seleccionado"
            }
          />
          {materials.length === 0 ? (
            <div
              className="flex h-24 flex-col items-center justify-center rounded-xl bg-slate-50 text-center text-xs text-slate-500"
              data-testid="analysis-materials-empty"
            >
              <Package className="mb-1 h-5 w-5 text-slate-400" />
              <span>Los materiales se capturan en la seccion &quot;Material utilizado&quot; de cada programa.</span>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="analysis-materials-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-2">Material</th>
                    <th className="py-2 pr-2 text-right">Cantidad</th>
                    <th className="py-2 pr-2">Unidad</th>
                    <th className="py-2 text-right">Turnos</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((m, i) => (
                    <tr
                      key={`${m.name}-${m.unit}`}
                      className="border-b border-slate-100 last:border-0"
                      data-testid={`analysis-materials-row-${i}`}
                    >
                      <td className="py-2 pr-2 font-semibold text-slate-800">{m.name}</td>
                      <td className="py-2 pr-2 text-right font-mono font-bold tabular-nums text-slate-900">
                        {m.quantity}
                      </td>
                      <td className="py-2 pr-2 text-slate-600">{m.unit}</td>
                      <td className="py-2 text-right font-mono tabular-nums text-slate-600">{m.shifts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-[11px] italic text-slate-500">
                Suma de todos los materiales capturados en los programas del paciente dentro del periodo. Util para reposicion y costeo.
              </p>
            </div>
          )}
        </Card>

        {chartData.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center py-8 text-center">
              <AlertTriangle className="mb-2 h-9 w-9 text-amber-400" />
              <h3 className="text-base font-bold text-slate-900">Sin datos en este rango</h3>
              <p className="mt-1 text-sm text-slate-500">
                Cambia el periodo o selecciona &quot;Todo el historial&quot; para ver mas turnos.
              </p>
            </div>
          </Card>
        ) : (
          <>
            {/* Tension arterial */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Tension arterial"
                subtitle="Rango de referencia adultos: sistolica 90-140 mmHg / diastolica 60-90 mmHg"
              />
              <div data-testid="chart-ta">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="mmHg"
                  yDomain={[40, 200]}
                  refBands={[
                    { y1: 90, y2: 140, fill: "#10B981" }, // sistolica normal
                  ]}
                  lines={[
                    { dataKey: "ta_sys", name: "Sistolica", stroke: "#DC2626" },
                    { dataKey: "ta_dia", name: "Diastolica", stroke: "#F59E0B" },
                  ]}
                />
              </div>
            </Card>

            {/* Frecuencia cardiaca */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Frecuencia cardiaca"
                subtitle="Rango de referencia adultos: 60-100 lpm"
              />
              <div data-testid="chart-fc">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="lpm"
                  yDomain={[40, 140]}
                  refBands={[
                    { y1: 60, y2: 100, fill: "#10B981" },
                  ]}
                  lines={[
                    { dataKey: "fc", name: "FC (lpm)", stroke: "#1D4ED8" },
                  ]}
                />
              </div>
            </Card>

            {/* Temperatura */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Temperatura corporal"
                subtitle="Normal 36.0-37.4 C / Febricula 37.5-37.9 / Fiebre >=38"
              />
              <div data-testid="chart-temp">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="C"
                  yDomain={[35, 40]}
                  refBands={[
                    { y1: 36, y2: 37.4, fill: "#10B981" },
                    { y1: 38, y2: 40, fill: "#DC2626" },
                  ]}
                  lines={[{ dataKey: "temp", name: "Temperatura", stroke: "#DC2626" }]}
                />
              </div>
            </Card>

            {/* SpO2 */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Saturacion de oxigeno (SpO2)"
                subtitle="Normal >=95% / Alerta 90-94% / Critico <90%"
              />
              <div data-testid="chart-spo2">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="%"
                  yDomain={[80, 100]}
                  refBands={[
                    { y1: 95, y2: 100, fill: "#10B981" },
                    { y1: 80, y2: 90, fill: "#DC2626" },
                  ]}
                  lines={[{ dataKey: "spo2", name: "SpO2 (%)", stroke: "#0891B2" }]}
                />
              </div>
            </Card>

            {/* EVA Dolor */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Dolor (EVA 0-10)"
                subtitle="0 = sin dolor / 1-3 leve / 4-6 moderado / 7-10 intenso"
              />
              <div data-testid="chart-eva">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="/10"
                  yDomain={[0, 10]}
                  refBands={[
                    { y1: 7, y2: 10, fill: "#DC2626" },
                  ]}
                  lines={[{ dataKey: "evaPain", name: "EVA dolor", stroke: "#7C3AED" }]}
                />
              </div>
            </Card>

            {/* Balance hidrico */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Balance hidrico"
                subtitle="Ingresos vs egresos (ml). Balance objetivo: 0 +/- 500 ml"
              />
              <div data-testid="chart-balance">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="ml"
                  refBands={[
                    { y1: -500, y2: 500, fill: "#10B981" },
                  ]}
                  refLines={[
                    { y: 0, stroke: "#64748B", label: "Balance 0" },
                  ]}
                  lines={[
                    { dataKey: "intake", name: "Ingresos", stroke: "#10B981" },
                    { dataKey: "output", name: "Egresos", stroke: "#F59E0B" },
                    { dataKey: "waterBalance", name: "Balance neto", stroke: "#1D4ED8" },
                  ]}
                />
              </div>
            </Card>

            {/* Sueno */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Horas de sueno"
                subtitle="Rango recomendado adulto mayor: 6-9 h por dia (banda verde)"
              />
              <div data-testid="chart-sleep">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="h"
                  yDomain={[0, 24]}
                  refBands={[
                    { y1: 6, y2: 9, fill: "#10B981" },
                  ]}
                  lines={[
                    { dataKey: "sleepHours", name: "Horas dormidas", stroke: "#4F46E5" },
                  ]}
                />
              </div>
            </Card>

            {/* Morse */}
            <Card>
              <SecHdr
                icon={Activity}
                title="Riesgo de caidas (Escala de Morse)"
                subtitle="0-24 bajo / 25-44 medio / >=45 alto"
              />
              <div data-testid="chart-morse">
                <TrendChart
                  data={chartData}
                  xKey="x"
                  unit="pts"
                  yDomain={[0, 125]}
                  refBands={[
                    { y1: 0, y2: 24, fill: "#10B981" },
                    { y1: 45, y2: 125, fill: "#DC2626" },
                  ]}
                  lines={[{ dataKey: "morseTotal", name: "Morse total", stroke: "#B91C1C" }]}
                />
              </div>
            </Card>
          </>
        )}

        <p className="pb-6 text-center text-[11px] text-slate-400">
          Los datos por turno se promedian cuando hay multiples tomas. Las bandas verdes/rojas indican rangos clinicos de referencia.
        </p>
      </main>
    </div>
  );
}
